import axios from 'axios';
import { store } from '../store';
import { logout, refreshThunk } from '../store/authSlice';
import { isNativeMobileRuntime } from '../utils/nativeRuntime';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
  // Required for httpOnly refresh-token cookie to be sent cross-origin
  withCredentials: true,
});

// ── Request interceptor: attach Bearer token ────────────────────
axiosInstance.interceptors.request.use(
  (config) => {
    if (typeof FormData !== 'undefined' && config.data instanceof FormData && config.headers) {
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
        config.headers.delete('content-type');
      } else {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }
    if (isNativeMobileRuntime()) {
      config.headers['X-Client-Platform'] = 'native-app';
    }
    const token = store.getState().auth.accessToken;
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor: handle 401 / refresh ────────────
let isRefreshing = false;
let failedQueue  = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) =>
    error ? p.reject(error) : p.resolve(token)
  );
  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;

    // Backend down / deploying → Nginx returns 503.
    // In native shell, avoid hard redirect because transient network wakeups can break DNS resolution.
    if (error.response?.status === 503) {
      if (!isNativeMobileRuntime()) {
        window.location.replace('/maintenance.html');
      }
      return Promise.reject(error);
    }

    // Network error (no response at all).
    // In native shell, surface the error instead of force-redirecting to avoid lock-in on flaky mobile networks.
    if (!error.response && error.code === 'ERR_NETWORK') {
      if (!isNativeMobileRuntime()) {
        window.location.replace('/maintenance.html');
      }
      return Promise.reject(error);
    }

    // Subscription expired restricted mode:
    // - If already authenticated, keep session and send user to dashboard (renew from there).
    // - If unauthenticated, redirect to login with reason.
    if (error.response?.status === 402) {
      const isAuthenticated = !!store.getState().auth?.accessToken;
      if (isAuthenticated) {
        if (window.location.pathname !== '/dashboard') {
          window.location.replace('/dashboard');
        }
      } else {
        store.dispatch(logout());
        window.location.replace('/login?reason=subscription_expired');
      }
      return Promise.reject(error);
    }

    // Never retry auth endpoints — login failures should show an error, not trigger
    // a refresh. Refresh endpoint itself must also be excluded to prevent loops.
    if (error.response?.status === 401 && !original._retry &&
        !original.url?.includes('/auth/refresh') &&
        !original.url?.includes('/auth/login') &&
        !original.url?.includes('/auth/mfa')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          original.headers.Authorization = `Bearer ${token}`;
          return axiosInstance(original);
        });
      }

      original._retry = true;
      isRefreshing = true;

      return store
        .dispatch(refreshThunk())
        .unwrap()
        .then((data) => {
          processQueue(null, data.accessToken);
          original.headers.Authorization = `Bearer ${data.accessToken}`;
          return axiosInstance(original);
        })
        .catch((err) => {
          processQueue(err, null);
          store.dispatch(logout());
          window.location.replace('/login');
          return Promise.reject(err);
        })
        .finally(() => {
          isRefreshing = false;
        });
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
